import { useEffect } from 'react';

import {
  AdyenPlatformExperience,
  DisputesOverview,
  PayoutsOverview,
  ReportsOverview,
} from '@adyen/adyen-platform-experience-web';
import { Box, Card } from '@mui/material';
import { useSnackbar } from 'notistack';

import LoadingOverlay from '../../../components/LoadingOverlay';
import { getErrorMessage } from '../../../lib/error';

type AdyenOverviewComponentClass = typeof PayoutsOverview | typeof ReportsOverview | typeof DisputesOverview;

type AdyenOverviewClassInstance = InstanceType<AdyenOverviewComponentClass>;

type AdyenSessionToken = {
  id: string;
  token: string;
};

type AdyenOverviewComponentProps = {
  isComponentInitialized: boolean;
  setIsComponentInitialized: (isComponentInitialized: boolean) => void;
  getAdyenSessionToken: () => Promise<AdyenSessionToken>;
  overviewClass: AdyenOverviewComponentClass;
  containerId: string;
  live: boolean;
  balanceAccountId?: string;
};

export default function AdyenOverviewComponent({
  isComponentInitialized,
  setIsComponentInitialized,
  getAdyenSessionToken,
  overviewClass,
  containerId,
  live,
  balanceAccountId,
}: AdyenOverviewComponentProps) {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let overviewInstance: AdyenOverviewClassInstance | null = null;

    const initializeAdyenOverview = async () => {
      try {
        setIsComponentInitialized(false);

        // Initialize Adyen platform experience with session token provider
        const adyenCore = await AdyenPlatformExperience({
          onSessionCreate: getAdyenSessionToken,
          environment: live ? 'live' : 'test',
        });

        overviewInstance = new overviewClass({
          core: adyenCore,
          balanceAccountId, // scopes to the specific balance account if provided
        });
        const containerSelector = `#${containerId}`;
        overviewInstance.mount(containerSelector);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    };

    initializeAdyenOverview();

    return () => {
      if (overviewInstance) {
        overviewInstance.unmount();
        overviewInstance = null;
      }
    };
  }, [
    getAdyenSessionToken,
    overviewClass,
    containerId,
    enqueueSnackbar,
    live,
    balanceAccountId,
    setIsComponentInitialized,
  ]);

  return (
    <Box id={containerId} sx={{ position: 'relative' }}>
      {!isComponentInitialized && (
        <Card sx={{ minHeight: 300, position: 'relative' }}>
          <LoadingOverlay />
        </Card>
      )}
    </Box>
  );
}
